<template>
  <div class="container">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h3 class="mb-0">
        <i class="fa fa-user-astronaut mr-2"></i>
        CitSci Admin Dashboard
      </h3>
    </div>

    <b-row class="mb-4" v-if="!statsLoading">
      <b-col sm="6" lg="3" class="mb-3 mb-lg-0">
        <a href="#" class="card h-100 text-decoration-none border" @click.prevent="filterByAll">
          <div class="card-body d-flex align-items-center">
            <div class="text-primary text-center mr-3" style="width: 40px;">
              <i class="fas fa-layer-group fa-2x"></i>
            </div>
            <div>
              <div class="h4 mb-0 font-weight-bold text-primary">{{ stats.totalProjects }}</div>
              <small class="text-muted text-uppercase">Total Projects</small>
            </div>
          </div>
        </a>
      </b-col>

      <b-col sm="6" lg="3" class="mb-3 mb-lg-0">
        <a href="#" class="card h-100 text-decoration-none border" @click.prevent="filterByFeatured">
          <div class="card-body d-flex align-items-center">
            <div class="text-warning text-center mr-3" style="width: 40px;">
              <i class="fa fa-star fa-2x"></i>
            </div>
            <div>
              <div class="h4 mb-0 font-weight-bold text-primary">{{ stats.featuredProjects }}</div>
              <small class="text-muted text-uppercase">Featured</small>
            </div>
          </div>
        </a>
      </b-col>

      <b-col sm="6" lg="3" class="mb-3 mb-lg-0">
        <a href="#" class="card h-100 text-decoration-none border" @click.prevent="filterByPublished">
          <div class="card-body d-flex align-items-center">
            <div class="text-success text-center mr-3" style="width: 40px;">
              <i class="fa fa-check-circle fa-2x"></i>
            </div>
            <div>
              <div class="h4 mb-0 font-weight-bold text-primary">{{ stats.publishedProjects }}</div>
              <small class="text-muted text-uppercase">Published</small>
            </div>
          </div>
        </a>
      </b-col>

      <b-col sm="6" lg="3">
        <a href="#" class="card h-100 text-decoration-none border" @click.prevent="filterByDraft">
          <div class="card-body d-flex align-items-center">
            <div class="text-secondary text-center mr-3" style="width: 40px;">
              <i class="fa fa-edit fa-2x"></i>
            </div>
            <div>
              <div class="h4 mb-0 font-weight-bold text-primary">{{ stats.draftProjects }}</div>
              <small class="text-muted text-uppercase">Drafts</small>
            </div>
          </div>
        </a>
      </b-col>
    </b-row>

    <div v-else class="text-center py-3 mb-4">
      <b-spinner small variant="primary"></b-spinner>
      <span class="ml-2 text-muted">Loading statistics...</span>
    </div>

    <hr class="mb-4">

    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="mb-0">
        Projects
        <small class="text-muted ml-2">{{ totalResults }} total</small>
      </h4>
    </div>

    <b-card class="mb-3">
      <b-row>
        <b-col md="5" class="mb-2 mb-md-0">
          <label class="small font-weight-bold mb-1 d-block">Search</label>
          <b-form-input
            v-model="localFilters.name"
            placeholder="Search by name..."
            @input="onSearchInput"
          ></b-form-input>
        </b-col>
        <b-col md="3" class="mb-2 mb-md-0">
          <label class="small font-weight-bold mb-1 d-block">Status</label>
          <b-form-select v-model="localFilters.projectState" :options="statusOptions" @change="applyFilters"></b-form-select>
        </b-col>
        <b-col md="3" class="mb-2 mb-md-0">
          <label class="small font-weight-bold mb-1 d-block">Featured</label>
          <b-form-select v-model="localFilters.isFeatured" :options="featuredOptions" @change="applyFilters"></b-form-select>
        </b-col>
        <b-col md="1" class="d-flex align-items-end">
          <b-button variant="outline-secondary" @click="resetFilters">
            Reset
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <div v-if="isLoading" class="text-center py-5">
      <b-spinner variant="primary" label="Loading..."></b-spinner>
      <p class="mt-2 text-muted">Loading projects...</p>
    </div>

    <!-- Results Table -->
    <b-card v-else>
      <b-table
        :items="projects"
        :fields="tableFields"
        :sort-by.sync="localSortBy"
        :sort-desc.sync="localSortDesc"
        :sort-direction="sortDirection"
        :sort-icon-left="true"
        striped
        hover
        responsive
        show-empty
        empty-text="No projects found"
        class="project-table mb-0"
        @sort-changed="onSortChanged"
      >
        <template #cell(name)="data">
          <strong>{{ data.item.name }}</strong>
        </template>
        <template #cell(projectState)="data">
          <b-badge :variant="getStateVariant(data.item.projectState)">
            {{ getStateLabel(data.item.projectState) }}
          </b-badge>
        </template>
        <template #cell(isFeatured)="data">
          <span v-if="data.item.isFeatured" class="text-warning">
            <i class="fa fa-star"></i> Featured
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #cell(createdAt)="data">
          {{ formatDate(data.item.createdAt) }}
        </template>
        <template #cell(updatedAt)="data">
          {{ formatDate(data.item.updatedAt) }}
        </template>
        <template #cell(actions)="data">
          <b-button
            size="sm"
            variant="secondary"
            @click="editProject(data.item)"
          >
            Edit
          </b-button>
        </template>
      </b-table>

      <!-- Infinite Scroll -->
      <div v-if="isLoadingMore" class="text-center py-3">
        <b-spinner small variant="primary"></b-spinner>
        <span class="ml-2 text-muted">Loading more projects...</span>
      </div>
      <div v-else-if="hasMore" ref="scrollBoundary" class="scroll-boundary"></div>
      <div v-else-if="projects.length > 0" class="text-center py-3 text-muted">
        <small>All {{ total }} projects loaded</small>
      </div>
    </b-card>

    <!-- Edit Project Modal -->
    <AdminProjectEditModal
      :visible="editModalVisible"
      :project-id="editProjectId"
      @close="onEditModalClose"
      @saved="onProjectSaved"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import AdminProjectEditModal from "./EditModal.vue";

export default {
  name: "AdminProjects",
  components: {
    AdminProjectEditModal,
  },
  data() {
    return {
      localFilters: {
        name: "",
        projectState: null,
        isFeatured: null,
      },
      localSortBy: "updatedAt",
      localSortDesc: true,
      searchDebounceTimer: null,
      scrollObserver: null,
      editModalVisible: false,
      editProjectId: null,
      statusOptions: [
        { value: null, text: "All" },
        { value: 0, text: "Draft" },
        { value: 1, text: "Published" },
      ],
      featuredOptions: [
        { value: null, text: "All" },
        { value: true, text: "Featured" },
        { value: false, text: "Not Featured" },
      ],
      tableFields: [
        { key: "name", label: "Project Name", sortable: true, class: "text-left" },
        { key: "projectState", label: "Status", sortable: false, class: "text-left" },
        { key: "isFeatured", label: "Featured", sortable: false, class: "text-left" },
        { key: "createdAt", label: "Created", sortable: true, class: "text-left" },
        { key: "updatedAt", label: "Updated", sortable: true, class: "text-left" },
        { key: "actions", label: "Actions", sortable: false, class: "text-right" },
      ],
    };
  },
  computed: {
    ...mapGetters("admin/projects", [
      "projects",
      "isLoading",
      "isLoadingMore",
      "page",
      "perPage",
      "total",
      "totalPages",
      "filters",
      "sortField",
      "sortDirection",
    ]),
    ...mapGetters("admin/stats", {
      statsLoading: "isLoading",
      stats: "stats",
    }),
    currentPage() {
      return this.page;
    },
    totalResults() {
      return this.total;
    },
    hasMore() {
      return this.projects.length < this.total;
    },
    sortDirection() {
      return this.localSortDesc ? "desc" : "asc";
    },
  },
  mounted() {
    // Check for URL query params
    const query = this.$route.query;
    if (query.name) {
      this.localFilters.name = query.name;
    }
    if (query.isFeatured === "true") {
      this.localFilters.isFeatured = true;
    } else if (query.isFeatured === "false") {
      this.localFilters.isFeatured = false;
    }
    if (query.projectState !== undefined) {
      this.localFilters.projectState = Number(query.projectState);
    }
    this.setFilters(this.localFilters);
    this.getStats();
    this.loadProjects().then(() => {
      this.$nextTick(() => {
        this.setupScrollObserver();
      });
    });
  },
  watch: {
    "$route.query": {
      handler(query) {
        this.localFilters.name = query.name || "";
        if (query.isFeatured === "true") {
          this.localFilters.isFeatured = true;
        } else if (query.isFeatured === "false") {
          this.localFilters.isFeatured = false;
        } else {
          this.localFilters.isFeatured = null;
        }
        if (query.projectState !== undefined) {
          this.localFilters.projectState = Number(query.projectState);
        } else {
          this.localFilters.projectState = null;
        }
        this.setFilters(this.localFilters);
      },
      deep: true,
    },
    filters: {
      handler(newFilters) {
        this.localFilters = { ...newFilters };
      },
      deep: true,
      immediate: true,
    },
  },
  beforeDestroy() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
    }
  },
  methods: {
    ...mapActions("admin/projects", [
      "getProjects",
      "loadMoreProjects",
      "setPage",
      "setFilters",
      "setSort",
      "clearFilters",
      "resetProjects",
    ]),
    ...mapActions("admin/stats", ["getStats"]),

    filterByAll() {
      this.localFilters = {
        name: "",
        projectState: null,
        isFeatured: null,
      };
      this.clearFilters();
      this.$router.push({ name: "AdminProjects" }).catch(() => {});
      this.loadProjects().then(() => {
        this.$nextTick(() => {
          this.setupScrollObserver();
        });
      });
    },

    filterByFeatured() {
      this.localFilters = {
        name: "",
        projectState: null,
        isFeatured: true,
      };
      this.clearFilters();
      this.setFilters(this.localFilters);
      this.$router.push({ name: "AdminProjects", query: { isFeatured: "true" } }).catch(() => {});
      this.loadProjects().then(() => {
        this.$nextTick(() => {
          this.setupScrollObserver();
        });
      });
    },

    filterByPublished() {
      this.localFilters = {
        name: "",
        projectState: 1,
        isFeatured: null,
      };
      this.clearFilters();
      this.setFilters(this.localFilters);
      this.$router.push({ name: "AdminProjects", query: { projectState: "1" } }).catch(() => {});
      this.loadProjects().then(() => {
        this.$nextTick(() => {
          this.setupScrollObserver();
        });
      });
    },

    filterByDraft() {
      this.localFilters = {
        name: "",
        projectState: 0,
        isFeatured: null,
      };
      this.clearFilters();
      this.setFilters(this.localFilters);
      this.$router.push({ name: "AdminProjects", query: { projectState: "0" } }).catch(() => {});
      this.loadProjects().then(() => {
        this.$nextTick(() => {
          this.setupScrollObserver();
        });
      });
    },

    async loadProjects() {
      await this.resetProjects();
      await this.getProjects();
    },

    setupScrollObserver() {
      if (this.scrollObserver) {
        this.scrollObserver.disconnect();
      }

      // https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
      this.scrollObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && this.hasMore && !this.isLoading && !this.isLoadingMore) {
            this.loadMore();
          }
        },
        { rootMargin: "100px" }
      );

      this.$nextTick(() => {
        if (this.$refs.scrollBoundary) {
          this.scrollObserver.observe(this.$refs.scrollBoundary);
        }
      });
    },

    async loadMore() {
      if (this.isLoadingMore || !this.hasMore) return;

      try {
        await this.loadMoreProjects();
        this.$nextTick(() => {
          this.setupScrollObserver();
        });
      } catch (error) {
        console.error("Error loading more projects:", error);
      }
    },

    onSearchInput() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.applyFilters();
      }, 1000);
    },

    applyFilters() {
      this.setFilters(this.localFilters);
      this.setPage(1);
      this.updateRouteQuery();
      this.loadProjects().then(() => {
        this.$nextTick(() => {
          this.setupScrollObserver();
        });
      });
    },

    updateRouteQuery() {
      const query = {};
      if (this.localFilters.name) {
        query.name = this.localFilters.name;
      }
      if (this.localFilters.projectState !== null) {
        query.projectState = String(this.localFilters.projectState);
      }
      if (this.localFilters.isFeatured !== null) {
        query.isFeatured = String(this.localFilters.isFeatured);
      }
      this.$router.push({ name: "AdminProjects", query }).catch(() => {});
    },

    resetFilters() {
      this.localFilters = {
        name: "",
        projectState: null,
        isFeatured: null,
      };
      this.clearFilters();
      this.$router.push({ name: "AdminProjects" }).catch(() => {});
      this.loadProjects().then(() => {
        this.$nextTick(() => {
          this.setupScrollObserver();
        });
      });
    },

    onSortChanged(ctx) {
      this.localSortBy = ctx.sortBy;
      this.localSortDesc = ctx.sortDesc;
      const direction = ctx.sortDesc ? "desc" : "asc";
      this.setSort({ field: ctx.sortBy, direction });
      this.setPage(1);
      this.loadProjects().then(() => {
        this.$nextTick(() => {
          this.setupScrollObserver();
        });
      });
    },

    editProject(project) {
      const id = project["@id"] ? project["@id"].split("/").pop() : project.id;
      this.editProjectId = id;
      this.editModalVisible = true;
    },

    onEditModalClose() {
      this.editModalVisible = false;
      this.editProjectId = null;
    },

    onProjectSaved() {
      // Reload projects list to reflect changes
      console.log('reloading')
      this.loadProjects().then(() => {
        this.$nextTick(() => {
          this.setupScrollObserver();
        });
      });
    },

    getStateLabel(state) {
      const labels = { 0: "Draft", 1: "Published", };
      return labels[state] || "Unknown";
    },

    getStateVariant(state) {
      const variants = { 0: "warning", 1: "success", 2: "secondary" };
      return variants[state] || "secondary";
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      return new Date(dateString).toLocaleDateString();
    },
  },
};
</script>

<style lang="scss" scoped>
.admin-projects {
  .stat-card {
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12) !important;
    }
  }

  .stat-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1976d2;
  }

  .stat-label {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .scroll-boundary {
    height: 1px;
  }
}
</style>
